import React, { useEffect, useRef, useState } from "react";
import { formatDateTime } from "../../utilities/globalServiceUtils";
import { useSite } from "../../context/siteContext";
import {
	Alert,
	Box,
	Button,
	IconButton,
	Tooltip,
	Typography,
	Divider
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Datagrid from "../Datagrid/Datagrid";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { useCart } from "../../context/cartContext";
import PageTitle from "../PageTitle/PageTitle";
import OrderTracker from "../OrderTracker/OrderTracker";
import OrderDetails from "../OrderDetails/OrderDetails";
import ContextFilterAndSort from "../ContextFilterAndSort/ContextFilterAndSort";
import "./AnalyticsTables.css";
import * as XLSX from "xlsx";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Swal from "sweetalert2";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import ContactCCRForm from "../ContactCCRForm/ContactCCRForm";
import { saveRequestList } from "../../services/productCardService";
import { PAGE_TITLES } from "../../constants";
import { ASSETS_BASE_URL } from "../../config";
import { useTranslation } from "react-i18next";
import { getAnalyticsOrders } from "../../services/analyticsChartService";
import ChartLoading from "../Carts/ChartLoading/ChartLoading";
import { Skeleton } from "@mui/material";
import ReplacementPartNotice from "../ReplacementPartNotice/ReplacementPartNotice";
import { getEffectiveProductDisplay } from "../../utilities/replacementProduct";
import analyticsKpiTooltips from "../Analytics/analyticsKpiTooltips.json";

const DEFAULT_PAGE_SIZE = 10;

const AnalyticsTables = ({
	chartFilters = {},
	setLoading,
	setShowCharts,
	selectedMetric,
	setSelectedMetric,
}) => {
	const { selectedSite, selectedSiteCurrency, currencySymbol } = useSite();
	const { addMultipleToCart } = useCart();
	const [metrics, setMetrics] = useState([]);
	const [metricsLoading, setMetricsLoading] = useState(false);
	const [orders, setOrders] = useState([]);
	const [fullOrdersData, setFullOrdersData] = useState([]);
	const [filteredOrders, setFilteredOrders] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedOrder, setSelectedOrder] = useState(null);
	const [, setError] = useState(null);
	const requestIdRef = useRef(0);
	const searchQueryRef = useRef("");
	const { t } = useTranslation();
	const [totalCount, setTotalCount] = useState(0);
	const [buyAgainOpen, setBuyAgainOpen] = useState(false);
	const [buyAgainBusy, setBuyAgainBusy] = useState(false);
	const [buyAgainSummary, setBuyAgainSummary] = useState({
		orderNumber: "",
		purchasable: [],
		nonPurchasable: [],
		notAvailable: [],
	});
	const [ccrOpen, setCcrOpen] = useState(false);
	const [ccrPrefill, setCcrPrefill] = useState(null);
	const [tableLoading, setTableLoading] = useState(false);
	// use global formatters
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const selectedMetricKey = selectedMetric?.key || "total_orders";
	const analyticsQueryKey = JSON.stringify({
		filters: chartFilters || {},
		metric: selectedMetricKey,
	});

	useEffect(() => {
		setPage(0);
		setSelectedOrder(null);
		setSearchQuery("");
		searchQueryRef.current = "";
	}, [analyticsQueryKey]);

	useEffect(() => {
		if (Object.keys(chartFilters || {}).length === 0) {
			setFullOrdersData([]);
			setOrders([]);
			setFilteredOrders([]);
			setTotalCount(0);
			setTableLoading(false);
			setMetricsLoading(false);
			setLoading(false);
			return;
		}

		const requestId = requestIdRef.current + 1;
		requestIdRef.current = requestId;
		const isCurrentRequest = () => requestIdRef.current === requestId;

		const fetchOrders = async (req_body) => {
			setLoading(true);
			setTableLoading(true);
			setMetricsLoading(page === 0);

			try {
				const reqBody = {
					...req_body,
					metric: selectedMetricKey,
					page,
					limit: pageSize,
					includeMetrics: page === 0,
				};

				const response = await getAnalyticsOrders(reqBody);
				if (!isCurrentRequest()) return;

				if (response.status) {
					const orderDetailsArray = response.data || [];
					const nextTotalCount = Number(
						response.totalCount || response?.pagination?.totalCount || 0,
					);
					const pageOrders = orderDetailsArray.map((data) => ({
						order_id: data.order.order_id,
						order_number: data.order.order_number,
						user_display_name: data.order.user_display_name,
						site_number:
							data.order.customer_site_number ||
							data.order.site_number ||
							"",
						customer_site_number:
							data.order.customer_site_number ||
							data.order.site_number ||
							"",
						site_id:
							data.order.customer_site_id ||
							data.order.site_id ||
							"",
						order_total: data.order.order_total,
						order_status: data.order.order_status,
						order_created_date: formatDateTime(data.order.order_created_date),
						order_update_date: formatDateTime(data.order.order_update_date),
						order_currency: data.order.order_currency,
						order_payment_mode: data.order.order_payment_mode,
						order_po: data?.details?.po_number || "",
						order_po_file: data?.details?.po_filename || "",
						products: data.products,
					}));

					const currentSearchQuery = searchQueryRef.current;
					const nextFilteredOrders = currentSearchQuery
						? pageOrders.filter((order) =>
							flattenObjectToString(order).includes(currentSearchQuery),
						)
						: pageOrders;

					setFullOrdersData(orderDetailsArray);
					setOrders(pageOrders);
					setFilteredOrders(nextFilteredOrders);
					setSelectedOrder(null);
					setTotalCount(nextTotalCount);

					if (response.order_metrics) {
						await aggregateOrderData(response);
					}

					setError(null);
				} else {
					setFullOrdersData([]);
					setOrders([]);
					setFilteredOrders([]);
					setTotalCount(0);
					setError(response.data?.message || t("UI_TEXT.NO_ORDERS_FOR_SITE"));
				}
			} catch (error) {
				console.error("Error while fetching the order", error);
				if (isCurrentRequest()) {
					setError(t("UI_TEXT.ERROR_OCCURRED_WHILE_FETCHING_ORDER"));
				}
			} finally {
				if (isCurrentRequest()) {
					setMetricsLoading(false);
					setTableLoading(false);
					setLoading(false);
				}
			}
		};

		fetchOrders(chartFilters);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [analyticsQueryKey, page, pageSize]);

	const aggregateOrderData = async (order_data) => {
		const hasOrderMetrics =
			order_data?.order_metrics &&
			Object.keys(order_data.order_metrics).length > 0;
		if (hasOrderMetrics) {
			const order_metrics = order_data.order_metrics;
			const trends = order_data.trends || {};
			await updateMetrics(order_metrics, trends, order_data.metrics_currency);
		} else {
			await updateMetrics();
		}
	};

	const flattenObjectToString = (obj) => {
		let flatString = "";

		// Recursively traverse the object and build a flat string of values
		const flatten = (value) => {
			if (typeof value === "object" && value !== null) {
				Object.values(value).forEach(flatten);
			} else {
				flatString += ` ${value}`;
			}
		};

		flatten(obj);
		return flatString.toLowerCase(); // Convert to lowercase for case-insensitive search
	};

	const handleSearchChange = (value) => {
		const query = value.toLowerCase();
		setSearchQuery(query);
		searchQueryRef.current = query;

		// Filter orders based on the search query
		const filtered = orders.filter((order) =>
			flattenObjectToString(order).includes(query),
		);

		setFilteredOrders(filtered);
	};
	const handleCloseOrderDetails = () => {
		setSelectedOrder(null);
	};

	const renderOrderDetails = () => {
		if (!selectedOrder) return null;
		const filteredOrder = fullOrdersData.find(
			(order) => order.order.order_id === selectedOrder.order_id,
		);
		const { order } = filteredOrder;

		filteredOrder.products = Array.from(
			new Map(
				filteredOrder.products.map((product) => [
					product.product_id,
					product,
				]),
			).values(),
		);

		return (
			<div>
				<PageTitle title={`Order Details: ${order.order_number}`} />
				<div className="track-order-container">
					<Button
						variant="outlined"
						color="primary"
						onClick={handleCloseOrderDetails}
					>
						{t("BUTTON.CLOSE")}
					</Button>
					<Button
						variant="contained"
						startIcon={<AddShoppingCartIcon />}
						color="primary"
						onClick={() => {
							handleOrderAgain(selectedOrder);
						}}
					>
						{t("BUTTON.BUY_AGAIN")}
					</Button>
				</div>
				<div className="order-tracker-container"></div>
				<OrderTracker orderData={filteredOrder} />
				<OrderDetails orderData={filteredOrder} />

				<div className="all-orders-container">
					<Button
						variant="contained"
						startIcon={<AddShoppingCartIcon />}
						color="primary"
						onClick={() => {
							handleOrderAgain(selectedOrder);
						}}
					>
						{t("BUTTON.BUY_AGAIN")}
					</Button>
					<Button
						variant="outlined"
						color="primary"
						onClick={handleCloseOrderDetails}
					>
						{t("BUTTON.CLOSE")}
					</Button>
				</div>
			</div>
		);
	};

	const exportOrdersToXLSX = (allOrders, selectedSite) => {
		const sheetData = [];
		const merges = [];

		// Define headers
		const headers = [
			t("ORDERS.ORDER_ID"),
			t("TABLE_HEADERS.ORDER_NUMBER"),
			t("TABLE_HEADERS.USERS"),
			t("TABLE_HEADERS.SITE_NUMBER"),
			t("TABLE_HEADERS.TOTAL"),
			t("TABLE_HEADERS.STATUS"),
			t("TABLE_HEADERS.CREATED_DATE"),
			t("TABLE_HEADERS.UPDATED_DATE"),
			t("TABLE_HEADERS.CURRENCY"),
			t("TABLE_HEADERS.PAYMENT_MODE"),
			t("TABLE_HEADERS.PO_NUMBER"),
			t("TABLE_HEADERS.PRODUCT_NAME"),
			t("UI_TEXT.PRODUCT_PRICE"),
			t("TABLE_HEADERS.QUANTITY"),
			t("TABLE_HEADERS.SITE"),
		];
		sheetData.push(headers);

		let rowIndex = 1; // Start from 1 due to header

		allOrders.forEach((order) => {
			const seen = new Set();
			const products =
				order.products?.filter((product) => {
					const id = product.product_id;
					const purchasable = product.productsData?.purchasable === 1;
					if (purchasable && !seen.has(id)) {
						seen.add(id);
						return true;
					}
					return false;
				}) || [];

			products.forEach((product, idx) => {
				const row = [];

				if (idx === 0) {
					// First product row: include order-level info
					row.push(
						order.order_id,
						order.order_number,
						order.user_display_name,
						order.site_number,
						order.order_total,
						order.order_status,
						order.order_created_date,
						order.order_update_date,
						order.order_currency,
						order.order_payment_mode,
						order.order_po,
					);
				} else {
					// Leave cells empty — will be merged
					for (let i = 0; i < 11; i++) row.push(null);
				}

				// Product-level data
				row.push(
					product.productsData.product_name || t("GENERAL.N_A"),
					`${currencySymbol}${product.productsData.product_sell_price}` ||
					t("GENERAL.N_A"),
					product.product_quantity,
					selectedSite,
				);

				sheetData.push(row);
				rowIndex++;
			});

			if (products.length > 1) {
				// Merge the order-level cells for this order's rows
				for (let col = 0; col <= 10; col++) {
					merges.push({
						s: { r: rowIndex - products.length, c: col }, // start row/col
						e: { r: rowIndex - 1, c: col }, // end row/col
					});
				}
			}
		});

		const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
		worksheet["!merges"] = merges;

		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, t("ORDERS.ORDERS"));
		XLSX.writeFile(
			workbook,
			`${t("ORDERS.ORDERS_EXPORT")}_${chartFilters.startDate}_to_${chartFilters.endDate}.xlsx`,
		);
	};

	const isPurchasableProduct = (product) => {
		const value =
			product?.productsData?.purchasable ??
			product?.purchasable ??
			product?.productsData?.is_purchasable ??
			product?.is_purchasable ??
			product?.isPurchasable;

		if (typeof value === "boolean") return value;
		if (typeof value === "number") return value === 1;
		if (typeof value === "string") {
			return ["1", "true", "yes"].includes(value.trim().toLowerCase());
		}
		return false;
	};

	const buildBuyAgainSummary = (row) => {
		const products = Array.isArray(row?.products) ? row.products : [];
		const purchasableMap = new Map();
		const nonPurchasableMap = new Map();
		const notAvailable = [];

		const addToMap = (map, item, qty) => {
			const key = `${item.product_id || "unknown"}:${item.machine_id || "na"}`;
			const existing = map.get(key);
			if (existing) existing.quantity += qty;
			else map.set(key, { ...item, quantity: qty, id: key });
		};

		products.forEach((product, index) => {
			const productData = product?.productsData || {};
			const product_id = productData?.product_id || product?.product_id;
			const machine_id = product?.machine_id || productData?.machine_id || row?.machine_id;
			const product_number =
				productData?.product_number ||
				product?.product_number ||
				product?.part_number ||
				product_id;
			const product_name =
				productData?.product_name ||
				product?.product_name ||
				product?.part_name ||
				`Product ${product_id || index + 1}`;
			const machine_number =
				productData?.machine_number ||
				product?.machine_number ||
				row?.machine_number ||
				"";
			const machine_name =
				productData?.machine_name ||
				product?.machine_name ||
				row?.machine_name ||
				"";
			const quantity = Number(product?.product_quantity || product?.quantity || 1) || 1;

			const baseItem = {
				product_id,
				machine_id,
				product_number,
				product_name,
				machine_number,
				machine_name,
				alternative_item_number:
					productData?.alternative_item_number || product?.alternative_item_number,
				replacement: productData?.replacement || product?.replacement,
			};

			if (!product_id || !machine_id) {
				notAvailable.push({
					...baseItem,
					id: `na-${index}`,
					quantity,
					reason: "Missing part or machine reference",
				});
				return;
			}

			if (isPurchasableProduct(productData) || isPurchasableProduct(product)) {
				addToMap(purchasableMap, baseItem, quantity);
			} else {
				addToMap(nonPurchasableMap, baseItem, quantity);
			}
		});

		return {
			orderNumber: row?.order_number || row?.order_id || "",
			purchasable: Array.from(purchasableMap.values()),
			nonPurchasable: Array.from(nonPurchasableMap.values()),
			notAvailable,
		};
	};

	const handleOrderAgain = (row) => {
		const summary = buildBuyAgainSummary(row);
		setBuyAgainSummary(summary);
		setBuyAgainOpen(true);
	};

	const addPurchasableToCart = async (items = []) => {
		const cartAdd = items.map((item) => ({
			ProductID: item.product_id,
			MachineID: item.machine_id,
			Quantity: item.quantity || 1,
			Site: selectedSite,
		}));

		if (cartAdd.length > 0) {
			await addMultipleToCart(cartAdd);
		}

		return cartAdd;
	};

	const addNonPurchasableToRequestList = async (items = []) => {
		const results = { added: 0, skipped: 0 };

		for (const item of items) {
			if (!item.machine_id || !item.product_id) {
				results.skipped += 1;
				continue;
			}

			try {
				const response = await saveRequestList({
					machine_id: item.machine_id,
					product_id: item.product_id,
					site_number: selectedSite,
				});

				if (response?.status) results.added += 1;
				else results.skipped += 1;
			} catch {
				results.skipped += 1;
			}
		}

		return results;
	};

	const handleBuyAgainAction = async (mode) => {
		const { purchasable, nonPurchasable, notAvailable, orderNumber } = buyAgainSummary;

		try {
			setBuyAgainBusy(true);

			let cartAdded = 0;
			let cartQty = 0;
			let requestAdded = 0;

			if (mode === "cart" || mode === "cart_and_request") {
				const cartAdd = await addPurchasableToCart(purchasable);
				cartAdded = cartAdd.length;
				cartQty = cartAdd.reduce((sum, item) => sum + Number(item.Quantity || 0), 0);
			}

			if (mode === "request" || mode === "cart_and_request") {
				const requestResult = await addNonPurchasableToRequestList(nonPurchasable);
				requestAdded = requestResult.added;
			}

			Swal.fire({
				icon: "success",
				title: `${t("BUTTON.BUY_AGAIN")} Summary${orderNumber ? ` - ${orderNumber}` : ""}`,
				html: `
					Cart added: <b>${cartAdded}</b> Qty: <b>${cartQty}</b><br/>
					Request List added: <b>${requestAdded}</b><br/>
					Not available: <b>${notAvailable.length}</b>
				`,
			});

			setBuyAgainOpen(false);
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: "Error",
				text: "Failed to add parts from this order.",
			});
		} finally {
			setBuyAgainBusy(false);
		}
	};

	// Define columns for the Datagrid
	const columns = [
		{
			field: "order_number",
			headerName: t("TABLE_HEADERS.ORDER_NUMBER"),
			flex: 1,
			width: 160,
			renderCell: (params) => (
				<Tooltip title={t("PLACEHOLDER.ORDER_DETAILS")}>
					<span
						style={{
							cursor: "pointer",
							fontWeight: "bold",
						}}
						onMouseEnter={(e) =>
							(e.target.style.fontWeight = "bold")
						}
						onMouseLeave={(e) =>
							(e.target.style.fontWeight = "bold")
						}
					>
						{params.value}
					</span>
				</Tooltip>
			),
		},
		{
			field: "order_payment_mode",
			headerName: t("TABLE_HEADERS.PAYMENT_MODE"),
			width: 160,
		},
		{ field: "order_po", headerName: "PO #", width: 150 },
		{
			field: "order_po_file",
			headerName: t("UI_TEXT.PO_FILE"),
			width: 160,
			renderCell: (params) =>
				params.value ? (
					<a
						href={`${ASSETS_BASE_URL.replace(/\/$/, "")}/po_uploads/${params.value}`}
						target="_blank"
						rel="noreferrer"
					>
						{params.value}
					</a>
				) : (
					""
				),
		},
		{ field: "user_display_name", headerName: "Order Placed By", width: 160 },
		{
			field: "order_total",
			headerName: "Total Order Value",
			width: 120,
		},
		{
			field: "order_currency",
			headerName: t("TABLE_HEADERS.CURRENCY"),
			width: 100,
			renderCell: (params) =>
				params.value?.toUpperCase() || t("UI_TEXT.USD"),
		},
		{
			field: "order_status",
			headerName: t("TABLE_HEADERS.STATUS"),
			width: 120,
		},
		{
			field: "order_created_date",
			headerName: t("TABLE_HEADERS.CREATED_DATE"),
			valueFormatter: (value) => formatDateTime(value),
			width: 150,
		},
		// {
		// 	field: "order_again",
		// 	headerName: t("TABLE_HEADERS.ORDER_AGAIN"),
		// 	width: 100,
		// 	sortable: false,
		// 	filterable: false,
		// 	headerAlign: "center",
		// 	align: "center",
		// 	renderCell: (params) => (
		// 		<Tooltip title={t("TABLE_HEADERS.ORDER_AGAIN")}>
		// 			<span>
		// 				<IconButton
		// 					color="primary"
		// 					aria-label="Order Again"
		// 					onClick={(event) => {
		// 						event.stopPropagation();
		// 						handleOrderAgain(params.row);
		// 					}}
		// 				>
		// 					<AddShoppingCartIcon />
		// 				</IconButton>
		// 			</span>
		// 		</Tooltip>
		// 	),
		// }
	];

	// const handleRowClick = (params) => {
	// 	handleViewOrder(params.row);
	// };
	const handleRowClick = () => { };
	const updateMetrics = async (order_metrics = {}, trends = {}, metricsCurrency = null) => {
		const hasData =
			Object.keys(order_metrics).length > 0 &&
			Object.keys(trends).length > 0;
		const metricCurrencySymbol = metricsCurrency?.symbol || currencySymbol;

		const formatCurrency = (value) => {
			const absValue = Math.abs(value);
			let formattedValue;
			let suffix = "";

			if (absValue >= 1e9) {
				formattedValue = value / 1e9;
				suffix = "B";
			} else if (absValue >= 1e6) {
				formattedValue = value / 1e6;
				suffix = "M";
			} else if (absValue >= 1e3) {
				formattedValue = value / 1e3;
				suffix = "K";
			} else {
				formattedValue = value;
			}

			return `${metricCurrencySymbol}${formattedValue.toFixed(2)}${suffix}`;
		};

		const createMetric = (title, value, change, changeType, key) => ({
			title,
			value,
			change: `${change}%`,
			changeType,
			key,
		});

		const revenueLabel = "Order Revenue";
		const ordersPlacedLabel = "# of Orders Placed";
		const invoicedOrdersLabel = "# of Invoiced Orders";
		const shippedOrdersLabel = "# of Shipped Orders";
		const averageDaysLabel = "Average Days from Order to Shipped";
		const formatDays = (value) => `${Number(value || 0).toFixed(1)} days`;

		const defaultMetrics = [
			createMetric(
				revenueLabel,
				formatCurrency(0),
				"0",
				"warning",
				"total_revenue",
			),
			createMetric(
				ordersPlacedLabel,
				"0",
				"0",
				"warning",
				"total_orders",
			),
			// createMetric("Shipped Orders", "0", "0", "warning"),
			createMetric(
				invoicedOrdersLabel,
				"0",
				"0",
				"warning",
				"Invoiced",
			),
			createMetric(
				shippedOrdersLabel,
				"0",
				"0",
				"warning",
				"shipped_or_delivered",
			),
			createMetric(
				t("METRICS.AVERAGE_ORDER_VALUE"),
				formatCurrency(0),
				"0",
				"warning",
				"AOV",
			),
			createMetric(
				averageDaysLabel,
				formatDays(0),
				"0",
				"warning",
				"average_days_order_to_shipped",
			),
		];

		if (!hasData) {
			setMetrics(defaultMetrics);
			return;
		}

		setMetrics([
			createMetric(
				revenueLabel,
				formatCurrency(order_metrics.total_revenue),
				`${trends.total_revenue.change}`,
				trends.total_revenue.trend,
				"total_revenue",
			),
			createMetric(
				ordersPlacedLabel,
				`${order_metrics.total_orders}`,
				`${trends.total_orders.change}`,
				trends.total_orders.trend,
				"total_orders",
			),
			createMetric(
				invoicedOrdersLabel,
				`${order_metrics?.total_orders_by_status?.invoiced || 0}`,
				`${trends?.total_orders_by_status?.invoiced?.change || 0}`,
				trends?.total_orders_by_status?.invoiced?.trend || "warning",
				"Invoiced",
			),
			createMetric(
				shippedOrdersLabel,
				`${order_metrics?.total_orders_by_status?.shipped_or_delivered || 0}`,
				`${trends?.total_orders_by_status?.shipped_or_delivered?.change || 0}`,
				trends?.total_orders_by_status?.shipped_or_delivered?.trend || "warning",
				"shipped_or_delivered",
			),
			createMetric(
				t("METRICS.AVERAGE_ORDER_VALUE"),
				formatCurrency(order_metrics.aov),
				`${trends.aov.change}`,
				trends.aov.trend,
				"AOV",
			),
			createMetric(
				averageDaysLabel,
				formatDays(order_metrics.average_days_order_to_shipped),
				`${trends?.average_days_order_to_shipped?.change || 0}`,
				trends?.average_days_order_to_shipped?.trend || "warning",
				"average_days_order_to_shipped",
			),
		]);
	};

	const handleShowTables = (metric) => {


		setSelectedMetric(metric);
		setShowCharts(false);
	};

	// Placeholder KPI slots (title/key only) so all cards render with their
	// shimmer while metrics are loading, instead of collapsing down to just
	// the one KPI that was clicked to get here.
	const placeholderMetrics = [
		{ title: "Order Revenue", value: "", change: "", changeType: "warning", key: "total_revenue" },
		{ title: "# of Orders Placed", value: "", change: "", changeType: "warning", key: "total_orders" },
		{ title: "# of Invoiced Orders", value: "", change: "", changeType: "warning", key: "Invoiced" },
		{ title: "# of Shipped Orders", value: "", change: "", changeType: "warning", key: "shipped_or_delivered" },
		{ title: t("METRICS.AVERAGE_ORDER_VALUE"), value: "", change: "", changeType: "warning", key: "AOV" },
		{ title: "Average Days from Order to Shipped", value: "", change: "", changeType: "warning", key: "average_days_order_to_shipped" },
	];
	const visibleMetrics = metrics.length > 0 ? metrics : placeholderMetrics;
	const loadingSkeletonRows = Math.max(
		1,
		Number.isFinite(Number(pageSize)) ? Number(pageSize) : DEFAULT_PAGE_SIZE,
	);

	const CustomLoadingOverlay = () => {
		return (
			<Box
				sx={{
					width: "100%",
					p: 2,
					backgroundColor: (theme) => theme.palette.background.paper,
				}}
			>
				{Array.from({ length: loadingSkeletonRows }).map((_, rowIndex) => (
					<Box
						key={rowIndex}
						sx={{
							display: "grid",
							gridTemplateColumns:
								"1.2fr 1fr 150px 160px 1fr 120px 100px 120px 150px 100px",
							columnGap: 2,
							alignItems: "center",
							py: 1.5,
							borderBottom: "1px solid rgba(224,224,224,0.4)",
						}}
					>
						{/* ORDER NUMBER */}
						<Skeleton
							variant="text"
							width="80%"
							height={32}
							sx={{ borderRadius: 1 }}
						/>

						{/* PAYMENT MODE */}
						<Skeleton
							variant="text"
							width="70%"
							height={32}
							sx={{ borderRadius: 1 }}
						/>

						{/* PO */}
						<Skeleton
							variant="text"
							width={110}
							height={32}
							sx={{ borderRadius: 1 }}
						/>

						{/* PO FILE */}
						<Skeleton
							variant="text"
							width={130}
							height={32}
							sx={{ borderRadius: 1 }}
						/>

						{/* USER */}
						<Skeleton
							variant="text"
							width="75%"
							height={32}
							sx={{ borderRadius: 1 }}
						/>

						{/* TOTAL */}
						<Skeleton
							variant="text"
							width={80}
							height={32}
							sx={{ borderRadius: 1 }}
						/>

						{/* CURRENCY */}
						<Skeleton
							variant="text"
							width={60}
							height={32}
							sx={{ borderRadius: 1 }}
						/>

						{/* STATUS */}
						<Skeleton
							variant="rounded"
							width={90}
							height={28}
							sx={{ borderRadius: "20px" }}
						/>

						{/* CREATED DATE */}
						<Skeleton
							variant="text"
							width={120}
							height={32}
							sx={{ borderRadius: 1 }}
						/>

						{/* ORDER AGAIN BUTTON */}
						<Box display="flex" justifyContent="center">
							<Skeleton
								variant="circular"
								width={36}
								height={36}
							/>
						</Box>
					</Box>
				))}
			</Box>
		);
	};
	const handleContactCCRFromBuyAgain = () => {
		const summary = buyAgainSummary || {};

		const allParts = [
			...(summary.purchasable || []),
			...(summary.nonPurchasable || []),
			...(summary.notAvailable || []),
		];

		const uniqueParts = Array.from(
			new Map(
				allParts.map((item) => [
					`${item.product_id || item.product_number}-${item.machine_id || item.machine_number}`,
					getEffectiveProductDisplay(item, {
						machineId: item.machine_id,
						currency: selectedSiteCurrency,
					}),
				]),
			).values(),
		);

		setCcrPrefill({
			order_number: summary.orderNumber || "",
			parts: uniqueParts.map((part) => ({
				product_id: part.product_id,
				product_number: part.product_number,
				product_name: part.product_name,
				machine_id: part.machine_id,
				machine_number: part.machine_number,
				quantity: part.quantity || 1,
			})),
		});

		setCcrOpen(true);
	};
	const buyAgainColumns = [
		{
			field: "product_number",
			headerName: t("LABELS.PART_NUMBER") || t("UI_TEXT.PART"),
			flex: 1,
			renderCell: (params) => (
				<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}>
					<span>{params.value}</span>
					<ReplacementPartNotice product={params.row} />
				</Box>
			),
		},
		{
			field: "product_name",
			headerName: t("TABLE_HEADERS.PART_NAME") || t("TABLE_HEADERS.PART_NAME"),
			flex: 2,
		},
		{
			field: "quantity",
			headerName: t("UI_TEXT.QTY") || t("UI_TEXT.QTY"),
			width: 90,
		},
		{
			field: "machine_number",
			headerName: t("TITLES.MACHINE_NUMBER") || t("TITLES.MACHINE_NUMBER"),
			flex: 1,
		},
	];
	return (
		<Box
			className="chart-container analytics-table-container"
			sx={{ px: 0 }}
			data-tour="analytics-data-point"
		>
			<Box className="analytics-kpi-row analytics-table-kpi-row">
				{visibleMetrics.map((metric, index) => (
					<Box
						key={index}
						className="chart-card analytics-kpi-card analytics-table-kpi-card"
						onClick={() => {

							handleShowTables(metric);
						}}
						sx={{
							boxShadow:
								selectedMetric?.title === metric.title
									? (theme) =>
										`inset 0 -4px 0 ${theme.palette.primary.main} !important`
									: "none !important",
						}}
					>
						<Box>
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
								<Typography variant="h6">{metric.title}</Typography>
								{analyticsKpiTooltips.order[metric.key] ? (
									<Tooltip
										title={analyticsKpiTooltips.order[metric.key]}
										arrow
										placement="top"
										slotProps={{
											tooltip: {
												sx: {
													fontSize: "13px",
													lineHeight: 1.4,
													maxWidth: 360,
													px: 1.25,
													py: 0.8,
												},
											},
										}}
									>
										<IconButton
											size="small"
											className="analytics-kpi-info-button"
											aria-label={`${metric.title} info`}
											onClick={(event) => event.stopPropagation()}
											sx={{ width: 14, height: 14, flex: "0 0 auto", color: "#ef233c", p: 0 }}
										>
											<InfoOutlinedIcon className="analytics-kpi-info-icon" />
										</IconButton>
									</Tooltip>
								) : null}
							</Box>
							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									columnGap: "15px",
									alignItems: "flex-start",
									justifyContent: "space-between",
									mt: 1,
								}}
							>
								{metricsLoading ? (
									<ChartLoading compact />
								) : (
									<Typography
										variant="h5"
										color="primary"
										sx={{
											fontWeight: 600,
											lineHeight: 1.05,
										}}
									>
										{metric.value}
									</Typography>
								)}
							</Box>
						</Box>
						<Box>
							{metricsLoading ? (
								<ChartLoading compact />
							) : metric.changeType === "success" ? (
								<>
									<TrendingUpIcon
										color="success"
										sx={{ fontSize: "30px" }}
									/>
									<Typography variant="body1" color="success">
										{metric.change}
									</Typography>
								</>
							) : metric.changeType === "error" ? (
								<>
									<TrendingDownIcon
										color="error"
										sx={{ fontSize: "30px" }}
									/>
									<Typography variant="body1" color="error">
										{metric.change}
									</Typography>
								</>
							) : (
								<>
									<TrendingFlatIcon
										sx={{
											fontSize: "30px",
											color: "text.secondary",
										}}
									/>
									<Typography
										variant="body1"
										sx={{ color: "text.secondary" }}
									>
										{metric.change}
									</Typography>
								</>
							)}
						</Box>
					</Box>
				))}
			</Box>
			<Box
				className="analytics-table-actions-row"
				sx={{
					display: { xs: "flex", xl: "none" },
				}}
			>
				<Button
					variant="text"
					color="primary"
					size="small"
					onClick={() => setShowCharts(true)}
					startIcon={<ArrowBackIcon />}
				>
					{t("BUTTON.BACK_TO_CHARTS", { defaultValue: "Back to Charts" })}
				</Button>
			</Box>
			<div className="orders-container">
				{/* <Box sx={{ mb: 2 }}>
					<Typography variant="h6">Orders</Typography>
					<Typography variant="body1">
						{t('GENERAL.SHOWING_1')} {filteredOrders.length} orders out of {fullOrdersData.length}
					</Typography>
				</Box> */}
				<PageTitle
					title={PAGE_TITLES.getFilteredTitle(
						selectedMetric?.title,
						t,
					)}
					subtitle={PAGE_TITLES.getFilteredSubtitle(
						filteredOrders.length,
						totalCount,
						t,
					)}
				/>
				<div
					className="orders-serach-container"
					style={{
						width: "100%",
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						gap: "16px",
						flexWrap: "wrap",
					}}
				>
					<Box sx={{ width: { xs: "100%", md: "20%" } }}>
						<ContextFilterAndSort
							value={searchQuery}
							onSearchChange={handleSearchChange}
							placeholder={t("PLACEHOLDER.SEARCH_ORDERS")}
							showSearch={true}
							showSort={false}
						/>
					</Box>

					<Button
						variant="contained"
						color="primary"
						size="small"
						startIcon={<FileDownloadIcon />}
						onClick={() =>
							exportOrdersToXLSX(filteredOrders, selectedSite)
						}
					>
						{t("BUTTON.EXPORT_ORDERS")}
					</Button>
				</div>
				<Datagrid
					data={filteredOrders}
					columns={columns}
					rowClick={true}
					onRowClick={handleRowClick}
					pageSize={pageSize}
					pageSizeOptions={[5, 10, 25, 50]}
					showSearchBar={false}
					checkboxSelection={false}
					rowIdField="order_id"
					sortBy={[{ field: "order_created_date", sort: "desc" }]}
					loading={tableLoading}
					slots={{
						loadingOverlay: CustomLoadingOverlay,
					}}
					paginationMode="server"
					rowCount={totalCount}
					paginationModel={{
						page,
						pageSize,
					}}
					onPaginationModelChange={(model) => {
						const nextPageSize = Number(model.pageSize) || pageSize;
						if (nextPageSize !== pageSize) {
							setPageSize(nextPageSize);
							setPage(0);
							return;
						}
						setPage(Number(model.page) || 0);
					}}
				/>
				{renderOrderDetails()}
				<ModalWrapper
					open={buyAgainOpen}
					setOpen={setBuyAgainOpen}
					width="70vw"
					title={`${t("BUTTON.BUY_AGAIN")}${buyAgainSummary.orderNumber ? ` - Order ${buyAgainSummary.orderNumber}` : ""}`}
					subtitle={t("ORDERS.REVIEW_PARTS_BEFORE_ADDING")}
					content={
						<Box sx={{ width: "100%" }}>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mb: 2, fontStyle: "italic", fontSize: "11px" }}
							>
								{t("ORDERS.PURCHASABLE_PARTS_INFO")}
							</Typography>

							{buyAgainSummary.purchasable.length === 0 &&
								buyAgainSummary.nonPurchasable.length === 0 && (
									<Alert severity="warning" sx={{ mb: 2 }}>
										Parts are not available to purchase. Please contact CCR for further details.
									</Alert>
								)}

							{buyAgainSummary.purchasable.length > 0 && (
								<Box sx={{ mb: 3 }}>
									<Typography variant="h6" gutterBottom fontWeight={600}>
										{t("UI_TEXT.PURCHASABLE_PARTS")} ({buyAgainSummary.purchasable.length})
									</Typography>
									<Datagrid
										data={buyAgainSummary.purchasable}
										columns={buyAgainColumns}
										rowIdField="id"
										showSearchBar={false}
									/>
								</Box>
							)}

							{buyAgainSummary.nonPurchasable.length > 0 && (
								<>
									<Divider sx={{ my: 2 }} />
									<Typography variant="h6" gutterBottom fontWeight={600}>
										{t("UI_TEXT.NON_PURCHASABLE_PARTS")} ({buyAgainSummary.nonPurchasable.length})
									</Typography>
									<Datagrid
										data={buyAgainSummary.nonPurchasable}
										columns={buyAgainColumns}
										rowIdField="id"
										showSearchBar={false}
									/>
								</>
							)}

							{buyAgainSummary.notAvailable.length > 0 && (
								<>
									<Divider sx={{ my: 2 }} />
									<Typography variant="h6" gutterBottom fontWeight={600}>
										{t("UI_TEXT.NOT_AVAILABLE_PARTS") || "Not Available Parts"} ({buyAgainSummary.notAvailable.length})
									</Typography>
									<Datagrid
										data={buyAgainSummary.notAvailable}
										columns={buyAgainColumns}
										rowIdField="id"
										showSearchBar={false}
									/>
								</>
							)}
						</Box>
					}
					actions={
						<>
							<Button
								variant="outlined"
								onClick={() => setBuyAgainOpen(false)}
								disabled={buyAgainBusy}
							>
								{t("BUTTON.CLOSE")}
							</Button>

							{buyAgainSummary.purchasable.length > 0 &&
								buyAgainSummary.nonPurchasable.length === 0 && (
									<Button
										variant="contained"
										onClick={() => handleBuyAgainAction("cart")}
										disabled={buyAgainBusy}
									>
										{t("BUTTONS.ADD_TO_CART")}
									</Button>
								)}

							{buyAgainSummary.purchasable.length > 0 &&
								buyAgainSummary.nonPurchasable.length > 0 && (
									<>
										<Button
											variant="contained"
											onClick={() => handleBuyAgainAction("cart")}
											disabled={buyAgainBusy}
										>
											{t("BUTTONS.ADD_TO_CART")}
										</Button>
										<Button
											variant="contained"
											onClick={() => handleBuyAgainAction("cart_and_request")}
											disabled={buyAgainBusy}
										>
											{t("BUTTONS.ADD_TO_CART_AND_REQUEST_LIST")}
										</Button>
										<Button
											variant="contained"
											onClick={() => handleBuyAgainAction("request")}
											disabled={buyAgainBusy}
										>
											{t("BUTTONS.ADD_TO_REQUEST_LIST")}
										</Button>
									</>
								)}

							{buyAgainSummary.purchasable.length === 0 &&
								buyAgainSummary.nonPurchasable.length > 0 && (
									<Button
										variant="contained"
										onClick={() => handleBuyAgainAction("request")}
										disabled={buyAgainBusy}
									>
										{t("BUTTONS.ADD_TO_REQUEST_LIST")}
									</Button>
								)}

							{buyAgainSummary.purchasable.length === 0 &&
								buyAgainSummary.nonPurchasable.length === 0 && (
									<Button
										variant="contained"
										onClick={handleContactCCRFromBuyAgain}
										disabled={buyAgainBusy}
									>
										{t("LABELS.CONTACT_CCR")}
									</Button>
								)}
						</>
					}
				/>

				<ContactCCRForm
					open={ccrOpen}
					setOpen={setCcrOpen}
					prefill={ccrPrefill}
				/>
			</div>

		</Box>
	);
};

export default AnalyticsTables;
